import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface PublicActivity {
  id: string;
  name: string;
  description?: string;
  start_time: string;
  end_time: string;
  days_of_week: number[];
  max_capacity?: number;
  requires_day_selection: boolean;
  current_enrollment?: number;
  schedule?: string;
  location?: string;
}

export const usePublicActivities = () => {
  const [activities, setActivities] = useState<PublicActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPublicActivities();
  }, []);

  const loadPublicActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Obtener actividades activas (sin requerir autenticación)
      const { data: activitiesData, error: activitiesError } = await supabase
        .from('activities')
        .select(`
          id,
          name,
          description,
          start_time,
          end_time,
          days_of_week,
          max_capacity,
          requires_day_selection
        `)
        .eq('status', 'active')
        .order('name');

      if (activitiesError) {
        throw activitiesError;
      }

      // Obtener conteo de estudiantes para cada actividad
      const { data: enrollmentData } = await supabase
        .from('student_activities')
        .select('activity_id')
        .eq('status', 'active');

      // Contar estudiantes por actividad
      const enrollmentCounts: Record<string, number> = {};
      (enrollmentData || []).forEach(enrollment => {
        enrollmentCounts[enrollment.activity_id] = (enrollmentCounts[enrollment.activity_id] || 0) + 1;
      });

      // Procesar actividades para agregar información adicional
      const processedActivities = (activitiesData || []).map(activity => {
        // Formatear horario
        const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const schedule = activity.days_of_week
          ?.map(day => dayNames[day])
          .join(', ') || 'Por confirmar';

        return {
          ...activity,
          current_enrollment: enrollmentCounts[activity.id] || 0,
          schedule: `${schedule} de ${activity.start_time} a ${activity.end_time}`,
          location: 'Ubicación por confirmar' // Placeholder
        };
      });

      setActivities(processedActivities);
    } catch (err: any) {
      setError(err.message);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  return { 
    activities, 
    loading, 
    error, 
    refetch: loadPublicActivities 
  };
};